/**
 * Secure Access → Access Gateways
 *
 * The PoP fleet. SuperAdmin sees and manages everything (create with the
 * dedicated/shared license decision, approve, decommission, claims). A tenant
 * admin gets a read-only view of the gateways assigned to their tenant — the
 * list itself is scoped by the API (VSNP-1125/1126).
 *
 * Active / Pending Approval tabs mirror the Edges page (VSNP-1096); row actions
 * (approve / decommission) reuse the shared icons.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, CheckCircle2, Trash2, Server, Radio, Users } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import StatCard from '../../components/common/StatCard';
import {
  BundleEpochCell,
  LogLevelCell,
  DenyRateCell,
  NumberCell,
  PercentCell,
  ThroughputCell,
} from '../../components/observability/MetricCells';
import accessObservabilityService, { type ByNode } from '../../services/accessObservability.service';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import RegisterNodeWizard from '../../components/access/RegisterNodeWizard';
import ClaimsPanel from '../../components/access/ClaimsPanel';
import { nodeStatusLabel } from '../../components/access/nodeStatus';
import ZtnaChip from '../../components/access/ZtnaChip';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  type AccessGateway,
  type GatewayFleetSummary,
} from '../../services/access.service';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { USER_TYPES } from '../../utils/constants';
import { NODE_IFACE_FILTER } from '../../utils/obsChart';

/** One node→value map per metric column (design §7.1). */
interface FleetMetrics {
  sessions: ByNode;
  flows: ByNode;
  cpu: ByNode;
  mem: ByNode;
  disk: ByNode;
  rx: ByNode;
  tx: ByNode;
  denyRate: ByNode;
  bundleEpoch: ByNode;
}

const EMPTY_METRICS: FleetMetrics = {
  sessions: {}, flows: {}, cpu: {}, mem: {}, disk: {}, rx: {}, tx: {}, denyRate: {}, bundleEpoch: {},
};

/** `undefined` (never reported) and 0 (reported idle) must not look alike. */
const at = (map: ByNode, node: string): number | null => (node in map ? map[node] : null);

const AccessGateways = () => {
  const toast = useToast();
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const tenants = useTenantStore((state) => state.tenants);

  const [gateways, setGateways] = useState<AccessGateway[]>([]);
  const [summary, setSummary] = useState<GatewayFleetSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const [decommissionTarget, setDecommissionTarget] = useState<AccessGateway | null>(null);
  const [decommissioning, setDecommissioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped whenever the wizard mints a token so the claims panel refetches.
  const [claimsRefresh, setClaimsRefresh] = useState(0);
  const [metrics, setMetrics] = useState<FleetMetrics>(EMPTY_METRICS);
  const [metricsLoading, setMetricsLoading] = useState(true);
  // Distinct from `error`, which is the gateway LIST failing (VSNP-1265). The
  // two are different incidents with different blast radii: the list failing
  // means the page is empty, the metrics failing means nine columns are, and a
  // page that shows one message for both teaches an operator to ignore it.
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [compiledEpoch, setCompiledEpoch] = useState<number | null>(null);
  // "All Tenants" (SuperAdmin, no tenant selected) has no single compiled
  // epoch to compare with: /policies/active needs a tenant and answered 400
  // on every refresh (VSNP-1434). There, each gateway's applied epoch is read
  // PER TENANT (the metric carries the label) and compared with THAT tenant's
  // compiled epoch, fetched once per tenant the reporting gateways serve.
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
  const [allTenantsView, setAllTenantsView] = useState(false);
  const [bundleByNodeTenant, setBundleByNodeTenant] = useState<Record<string, Record<string, number>>>({});
  const [compiledByTenant, setCompiledByTenant] = useState<Record<string, number>>({});

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // The list is tenant-scoped by the API (VSNP-1125): a tenant admin gets
      // exactly the gateways assigned to their tenant. The fleet summary stays
      // SuperAdmin-only, so don't request it for anyone else.
      const [list, fleet] = await Promise.all([
        accessService.listGateways({ limit: 200 }),
        isSuperAdmin ? accessService.getGatewayFleetSummary() : Promise.resolve(null),
      ]);
      setGateways(list?.items ?? []);
      setSummary(fleet ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the gateway fleet');
      setGateways([]);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  // ---------------------------------------------------------------------
  // Health and load columns (design §7.1, VSNP-1219)
  // ---------------------------------------------------------------------
  //
  // ONE QUERY PER COLUMN, each aggregated `by (node)`, never one call per row:
  // a 40-gateway fleet asks nine questions, not 360. The tenant matcher and the
  // node allow-list are injected SERVER-SIDE by the observability proxy
  // (design §6.3) — nothing here names a tenant, deliberately.
  const fetchMetrics = useCallback(async () => {
    // Read from the store at call time — getTenantId is what tenantParams()
    // sends, so this is exactly "would /policies/active carry a tenant".
    const allTenants = isSuperAdmin && !useTenantStore.getState().getTenantId();
    try {
      setMetricsLoading(true);
      const [
        sessions, flows, cpu, mem, disk, rx, tx, denyRate, bundleEpoch, bundlePerTenant, activePolicy,
      ] = await Promise.all([
        accessObservabilityService.instantByNode('sum by (node) (agw_sessions_live)'),
        accessObservabilityService.instantByNode('sum by (node) (agw_flows_active)'),
        accessObservabilityService.instantByNode('max by (node) (node_cpu_usage_percent{node_kind="gateway"})'),
        accessObservabilityService.instantByNode(
          '100 * max by (node) (node_memory_used_bytes{node_kind="gateway"} / node_memory_total_bytes)',
        ),
        // The audit spool's mount is the one that matters (design §6.1). Where
        // the spool is not a separate filesystem the query falls back to `/`,
        // and `max` then reports the fuller of the two — the conservative
        // reading for a column an operator scans for trouble.
        accessObservabilityService.instantByNode(
          '100 * max by (node) (node_filesystem_used_bytes{mount=~"/var/lib/securelink|/"} / node_filesystem_size_bytes)',
        ),
        // The interface filter is shared with Gateway detail's per-NIC chart
        // (VSNP-1262): the two used to differ, so a full-tunnel gateway's
        // fleet row and its own Traffic tab disagreed — and the fleet number
        // double-counted, since tunnel bytes also cross the physical NIC.
        accessObservabilityService.instantByNode(
          `sum by (node) (rate(node_network_receive_bytes_total{${NODE_IFACE_FILTER}}[5m])) * 8 / 1000000`,
        ),
        accessObservabilityService.instantByNode(
          `sum by (node) (rate(node_network_transmit_bytes_total{${NODE_IFACE_FILTER}}[5m])) * 8 / 1000000`,
        ),
        accessObservabilityService.instantByNode('sum by (node) (rate(agw_flow_decisions_total{action="deny"}[1h]))'),
        accessObservabilityService.instantByNode('max by (node) (agw_policy_bundle_epoch)'),
        allTenants
          ? accessObservabilityService.instantByNodeAnd('max by (node, tenant) (agw_policy_bundle_epoch)', 'tenant')
          : Promise.resolve({} as Record<string, Record<string, number>>),
        // The compiled epoch comes from the orchestrator's own record rather
        // than from `access_policy_active_epoch`: that series is scraped from
        // this API's registry, and an environment without a vmagent (dev-1) has
        // it nowhere in the metrics store while the number itself is right here.
        // Not asked in "All Tenants": there is no tenant to ask it for.
        allTenants ? Promise.resolve(null) : accessService.getActivePolicy().catch(() => null),
      ]);
      let compiled: Record<string, number> = {};
      if (allTenants) {
        // One call per tenant the reporting gateways serve — the tenants their
        // epoch series are labelled with. An unlabelled series (a gateway that
        // reports its bundle under an empty tenant key) has no tenant to
        // compare with and is left without a badge rather than guessed.
        const tenantIds = [...new Set(Object.values(bundlePerTenant).flatMap((m) => Object.keys(m)))]
          .filter(Boolean)
          .slice(0, 50);
        const answers = await Promise.all(
          tenantIds.map(async (t) => [t, (await accessService.getActivePolicy(t).catch(() => null))?.epoch] as const),
        );
        compiled = Object.fromEntries(answers.filter((a): a is readonly [string, number] => typeof a[1] === 'number'));
      }
      setMetrics({ sessions, flows, cpu, mem, disk, rx, tx, denyRate, bundleEpoch });
      setAllTenantsView(allTenants);
      setBundleByNodeTenant(bundlePerTenant);
      setCompiledByTenant(compiled);
      setCompiledEpoch(activePolicy?.epoch ?? null);
      setMetricsError(null);
    } catch (err) {
      // A metrics outage must not blank the fleet table: the DB-backed columns
      // still answer "which gateways exist and are they enrolled".
      //
      // But it must SAY so (VSNP-1265). An empty metrics map and a failed query
      // both render as `—`, and a column of em dashes with nothing else on the
      // page reads as "these gateways are idle" — the opposite of the truth.
      // The error is surfaced rather than swallowed, and the stale numbers are
      // dropped rather than left to look current.
      setMetrics(EMPTY_METRICS);
      setBundleByNodeTenant({});
      setCompiledByTenant({});
      setMetricsError(
        err instanceof Error && err.message
          ? err.message
          : 'The metrics store did not answer',
      );
    } finally {
      setMetricsLoading(false);
    }
  }, [isSuperAdmin]);

  // selectedTenantId: re-run (and re-arm the timer) when the header's tenant
  // selection changes, since fetchMetrics reads the selection at call time.
  useEffect(() => {
    void fetchMetrics();
    const timer = setInterval(() => void fetchMetrics(), 30000);
    return () => clearInterval(timer);
  }, [fetchMetrics, selectedTenantId]);

  const handleApprove = async (gateway: AccessGateway) => {
    try {
      await accessService.approveGateway(gateway.id);
      toast.success('Gateway approved', gateway.name ?? gateway.node_id);
      await fetchAll();
    } catch (err) {
      toast.error('Approve failed', err instanceof Error ? err.message : 'Could not approve the gateway');
    }
  };

  const handleDecommission = async () => {
    if (!decommissionTarget) return;
    try {
      setDecommissioning(true);
      await accessService.decommissionGateway(decommissionTarget.id);
      toast.success('Gateway decommissioned', decommissionTarget.name ?? decommissionTarget.node_id);
      setDecommissionTarget(null);
      await fetchAll();
    } catch (err) {
      toast.error('Decommission failed', err instanceof Error ? err.message : 'Could not decommission the gateway');
    } finally {
      setDecommissioning(false);
    }
  };

  const dateFormat = useSettingsStore.getState().getDateFormatSettings();
  const tenantName = (id: string | null) => tenants.find((t) => t.id === id)?.name ?? id ?? '—';

  /**
   * What the Bundle cell compares. Scoped to a tenant: the node's epoch
   * against that tenant's compiled epoch, as before. "All Tenants": the node's
   * epoch PER TENANT against each tenant's own compiled epoch — the first
   * tenant it is behind for is shown with the badge, else its newest epoch.
   */
  const bundleFor = (node: string): { nodeEpoch: number | null; compiledEpoch: number | null; tenant: string | null } => {
    if (!allTenantsView) return { nodeEpoch: at(metrics.bundleEpoch, node), compiledEpoch, tenant: null };
    const perTenant = Object.entries(bundleByNodeTenant[node] ?? {});
    const behind = perTenant.find(([t, e]) => t in compiledByTenant && e < compiledByTenant[t]);
    if (behind) return { nodeEpoch: behind[1], compiledEpoch: compiledByTenant[behind[0]], tenant: tenantName(behind[0]) };
    return { nodeEpoch: at(metrics.bundleEpoch, node), compiledEpoch: null, tenant: null };
  };

  const columns: Column<AccessGateway>[] = [
    {
      key: 'name',
      label: 'Gateway',
      sortable: true,
      render: (gw) => (
        <Link to={`/access/gateways/${gw.id}`} className="block">
          <div className="font-medium text-gray-900 hover:text-primary-600">{gw.name ?? '—'}</div>
          <div className="text-xs text-gray-500 font-mono">{gw.node_id.slice(0, 18)}…</div>
        </Link>
      ),
    },
    {
      key: 'sharing_mode',
      label: 'License',
      sortable: true,
      render: (gw) =>
        gw.sharing_mode === 'shared' ? (
          <div>
            <span className="rounded bg-info-100 px-1.5 py-0.5 text-xs font-medium text-info-700">shared</span>
            <div className="mt-0.5 text-xs text-gray-500">
              {gw.current_tenants}/{gw.max_tenants} tenants
            </div>
          </div>
        ) : (
          <div>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">dedicated</span>
            {isSuperAdmin && (
              <div className="mt-0.5 max-w-[140px] truncate text-xs text-gray-500" title={tenantName(gw.owner_tenant_id)}>
                {tenantName(gw.owner_tenant_id)}
              </div>
            )}
          </div>
        ),
    },
    {
      key: 'pop_code',
      label: 'PoP',
      sortable: true,
      render: (gw) => gw.pop_code ?? gw.region ?? '—',
    },
    {
      key: 'public_endpoint',
      label: 'Endpoint',
      render: (gw) => (gw.public_endpoint ? `${gw.public_endpoint}:${gw.quic_port}` : '—'),
    },
    { key: 'version', label: 'Version', sortable: true, render: (gw) => gw.version ?? '—' },
    {
      key: 'bundle_epoch',
      label: 'Bundle',
      render: (gw) => <BundleEpochCell {...bundleFor(gw.node_id)} />,
    },
    {
      key: 'effective_log_level',
      label: 'Log level',
      render: (gw) => (
        <LogLevelCell
          effective={gw.effective_log_level}
          intent={gw.log_level}
          override={gw.log_level_override}
          since={gw.effective_log_level_since}
        />
      ),
    },
    {
      key: 'last_session_count',
      label: 'Sessions',
      sortable: true,
      // Live from the metrics plane; the status heartbeat's count is the
      // fallback so the column is never empty just because VM is behind.
      render: (gw) => <NumberCell value={at(metrics.sessions, gw.node_id) ?? gw.last_session_count ?? null} />,
    },
    {
      key: 'flows',
      label: 'Flows',
      render: (gw) => <NumberCell value={at(metrics.flows, gw.node_id) ?? gw.last_flow_count ?? null} />,
    },
    { key: 'cpu', label: 'CPU', render: (gw) => <PercentCell value={at(metrics.cpu, gw.node_id)} /> },
    { key: 'mem', label: 'Mem', render: (gw) => <PercentCell value={at(metrics.mem, gw.node_id)} warn={85} danger={95} /> },
    {
      key: 'disk',
      label: 'Disk',
      className: 'whitespace-nowrap',
      render: (gw) => <PercentCell value={at(metrics.disk, gw.node_id)} warn={80} danger={90} />,
    },
    {
      key: 'throughput',
      label: 'Rx / Tx',
      render: (gw) => <ThroughputCell rx={at(metrics.rx, gw.node_id)} tx={at(metrics.tx, gw.node_id)} />,
    },
    {
      key: 'deny_rate',
      label: 'Denies (1h)',
      render: (gw) => <DenyRateCell perSecond={at(metrics.denyRate, gw.node_id)} />,
    },
    { key: 'last_health', label: 'Health', sortable: true, render: (gw) => <StatusBadge status={gw.last_health} /> },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (gw) => <StatusBadge status={nodeStatusLabel(gw.status, gw.certificate_serial_number)} />,
    },
    {
      key: 'last_seen',
      label: 'Last Seen',
      sortable: true,
      render: (gw) => (gw.last_seen ? formatDate(gw.last_seen, dateFormat) : 'never'),
    },
    {
      key: 'actions',
      label: '',
      render: (gw) => isSuperAdmin && (
        <div className="flex items-center justify-end gap-1">
          {gw.status === 'pending_approval' && gw.certificate_serial_number && (
            <button
              type="button"
              className="p-1.5 rounded hover:bg-success-50 text-success-600"
              title="Approve gateway"
              onClick={() => void handleApprove(gw)}
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
          {gw.status !== 'decommissioned' && (
            <button
              type="button"
              className="p-1.5 rounded hover:bg-danger-50 text-gray-400 hover:text-danger-600"
              title="Decommission gateway"
              onClick={() => setDecommissionTarget(gw)}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const activeCount = summary?.byStatus?.active ?? 0;
  const totalSessions = gateways.reduce((sum, gw) => sum + (gw.last_session_count ?? 0), 0);
  const versionCount = Object.keys(summary?.byVersion ?? {}).length;
  // Plain computation (no hook): this sits below the !isSuperAdmin early return.
  const nodeNames = Object.fromEntries(gateways.map((gw) => [gw.id, gw.name ?? gw.node_id]));
  const pending = gateways.filter((gw) => gw.status === 'pending_approval');
  const active = gateways.filter((gw) => gw.status !== 'pending_approval');
  const shown = activeTab === 'pending' ? pending : active;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Access Gateways</h1>
            <ZtnaChip />
          </div>
          <p className="text-gray-600 mt-1">
            {isSuperAdmin
              ? 'PoP fleet terminating client QUIC/MASQUE tunnels — dedicated or shared per license'
              : 'Gateways serving your tenant (managed by the platform operator)'}
          </p>
          {isSuperAdmin && (
            <p className="text-xs text-gray-400 mt-1">
              Looking for v2 shared gateways (MTGE)? → Equipment → Shared Gateways
            </p>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void fetchAll();
              void fetchMetrics();
            }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading || metricsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {isSuperAdmin && (
            <button type="button" className="btn-primary" onClick={() => setIsAddOpen(true)}>
              <Plus className="w-4 h-4 mr-2 inline" />
              Create Gateway
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      {metricsError && (
        // Warning, not danger: the fleet list below is still correct and still
        // actionable. Only the live columns are missing, and the banner says
        // which — so `—` is read as "not measured", not as "zero".
        <div
          className="rounded-md border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-800"
          data-testid="gw-metrics-error"
          role="status"
        >
          Live metrics are unavailable, so the load columns (sessions, flows, CPU, memory,
          disk, throughput, deny rate, bundle epoch) show no value rather than zero.
          The gateway list itself is current. <span className="text-warning-700">({metricsError})</span>
        </div>
      )}

      {isSuperAdmin && (
      <div className="grid grid-cols-4 gap-4">
        <StatCard title="Gateways" value={summary?.total ?? gateways.length} icon={Server} color="primary" loading={loading} />
        <StatCard title="Active" value={activeCount} icon={Radio} color="success" loading={loading} />
        <StatCard title="Sessions" value={totalSessions} icon={Users} color="info" loading={loading} />
        <StatCard
          title="Versions in Fleet"
          value={versionCount}
          icon={Server}
          color={versionCount > 1 ? 'warning' : 'secondary'}
          subtitle={versionCount > 1 ? 'Fleet is not on a single version' : undefined}
          loading={loading}
        />
      </div>
      )}

      {/* Tabs (Edges pattern) */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'active'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Gateways
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center space-x-2 ${
              activeTab === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>Pending Approval</span>
            {pending.length > 0 && (
              <span
                className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none rounded-full ${
                  activeTab === 'pending' ? 'bg-primary-100 text-primary-700' : 'bg-orange-100 text-orange-700 animate-pulse'
                }`}
              >
                {pending.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={shown}
          loading={loading}
          searchPlaceholder="Search gateways…"
          emptyMessage={
            activeTab === 'pending'
              ? 'No gateways awaiting approval'
              : 'No gateways yet — Create Gateway makes the record and hands you a one-time enrolment token for the PoP host.'
          }
        />
      </div>

      {isSuperAdmin && <ClaimsPanel deviceType="gateway" nodeNames={nodeNames} refreshKey={claimsRefresh} />}

      <RegisterNodeWizard
        kind="gateway"
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false);
          setClaimsRefresh((n) => n + 1);
        }}
        onRegistered={() => void fetchAll()}
        tenants={tenants.map((t) => ({ id: t.id, name: t.name }))}
      />

      <ConfirmDialog
        isOpen={!!decommissionTarget}
        onClose={() => setDecommissionTarget(null)}
        onConfirm={handleDecommission}
        title="Decommission gateway"
        message={`Decommission '${decommissionTarget?.name ?? decommissionTarget?.node_id}'? Clients will stop being brokered through this PoP. The record and its status history are kept.`}
        confirmText="Decommission"
        variant="danger"
        isLoading={decommissioning}
      />
    </div>
  );
};

export default AccessGateways;
